import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  Chip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  Stack,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getCategories,
  getSubCategories,
  createDocument,
} from '../../endpoints/documents';
import { DocumentCreate as DocumentCreateType } from '../../types/document';
import { GenericResponse } from '../../types/generic';

const steps = ['Document Information', 'Categories & Tags', 'Verify & Create'];

const DocumentCreate: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<DocumentCreateType>({
    name: '',
    version: '',
    description: '',
    category_ids: [],
    sub_category_ids: [],
    tags: [],
  });

  // Separate state for raw tag input to allow typing commas and spaces
  const [rawTagInput, setRawTagInput] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch categories and sub-categories
  const { data: categories = [] } = useApiGet(['categories'], getCategories);
  const { data: subCategories = [] } = useApiGet(
    ['sub-categories'],
    getSubCategories
  );

  // Filter sub-categories based on selected categories
  const filteredSubCategories = subCategories.filter((subCat) =>
    formData.category_ids.includes(subCat.category_id)
  );

  // Create document mutation
  const { mutate: createDocumentMutation, isPending: isCreating } =
    useApiSend<GenericResponse>(
      createDocument,
      (data) => {
        // Navigate to DocumentManager page after successful creation
        navigate(`/library/${data.message}/edit`);
      },
      (error) => {
        console.error('Error creating document:', error);
        // Handle error - could show a toast notification
      },
      [['documents-library']]
    );

  const handleInputChange = (field: keyof DocumentCreateType, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedCategories =
      typeof value === 'string' ? value.split(',') : value;
    handleInputChange('category_ids', selectedCategories);

    // Clear sub-categories that don't belong to the new selected categories
    if (selectedCategories.length === 0) {
      handleInputChange('sub_category_ids', []);
    } else {
      const currentSubCategories = formData.sub_category_ids;
      const validSubCategories = currentSubCategories.filter((subCatId) => {
        const subCat = subCategories.find((sc) => sc.id === subCatId);
        return subCat && selectedCategories.includes(subCat.category_id);
      });
      handleInputChange('sub_category_ids', validSubCategories);
    }
  };

  const handleSubCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedSubCategories =
      typeof value === 'string' ? value.split(',') : value;
    handleInputChange('sub_category_ids', selectedSubCategories);
  };

  const removeCategory = (categoryId: string) => {
    const newCategories = formData.category_ids.filter(
      (id) => id !== categoryId
    );
    handleInputChange('category_ids', newCategories);

    // Also remove sub-categories that don't belong to remaining categories
    const remainingSubCategories = formData.sub_category_ids.filter(
      (subCatId) => {
        const subCat = subCategories.find((sc) => sc.id === subCatId);
        return subCat && newCategories.includes(subCat.category_id);
      }
    );
    handleInputChange('sub_category_ids', remainingSubCategories);
  };

  const removeSubCategory = (subCategoryId: string) => {
    const newSubCategories = formData.sub_category_ids.filter(
      (id) => id !== subCategoryId
    );
    handleInputChange('sub_category_ids', newSubCategories);
  };

  const handleTagsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRawTagInput(value);

    // Parse tags from the raw input for storage
    const parsedTags = value
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
    handleInputChange('tags', parsedTags);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 0) {
      if (!formData.name.trim()) {
        newErrors.name = 'Document name is required';
      }
      if (!formData.version.trim()) {
        newErrors.version = 'Version is required';
      }
    }

    if (step === 1) {
      if (formData.category_ids.length === 0) {
        newErrors.category_ids = 'At least one category is required';
      }

      // Validate tags format - only if there are tags to validate
      if (formData.tags.length > 0) {
        const invalidTags = formData.tags.filter((tag) => {
          // Check for tags with spaces (should be single words)
          if (tag.includes(' ')) {
            return true;
          }
          // Check for tags that are too long
          if (tag.length > 50) {
            return true;
          }
          // Check for tags with special characters (allow alphanumeric and hyphens)
          if (!/^[a-zA-Z0-9-]+$/.test(tag)) {
            return true;
          }
          return false;
        });

        if (invalidTags.length > 0) {
          newErrors.tags = `Invalid tags: ${invalidTags.join(', ')}. Tags should be single words with only letters, numbers, and hyphens.`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = () => {
    if (validateStep(activeStep)) {
      createDocumentMutation(formData);
    }
  };

  const getCategoryName = (id: string) => {
    return categories.find((cat) => cat.id === id)?.name || id;
  };

  const getSubCategoryName = (id: string) => {
    return subCategories.find((subCat) => subCat.id === id)?.name || id;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/library"
          variant="body2"
          sx={{ cursor: 'pointer' }}
        >
          Documents
        </Link>
        <Typography variant="body2" color="text.primary">
          Create New Document
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Create New Document
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                <Box sx={{ mb: 2 }}>
                  {index === 0 && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Document Name"
                          value={formData.name}
                          onChange={(e) =>
                            handleInputChange('name', e.target.value)
                          }
                          error={!!errors.name}
                          helperText={errors.name}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Version"
                          value={formData.version}
                          onChange={(e) =>
                            handleInputChange('version', e.target.value)
                          }
                          error={!!errors.version}
                          helperText={errors.version}
                          placeholder="e.g., 1.0, 2024.1"
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Description"
                          value={formData.description}
                          onChange={(e) =>
                            handleInputChange('description', e.target.value)
                          }
                          multiline
                          rows={3}
                          placeholder="Brief description of the document..."
                        />
                      </Grid>
                    </Grid>
                  )}

                  {index === 1 && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={!!errors.category_ids}>
                          <InputLabel>Categories *</InputLabel>
                          <Select
                            multiple
                            value={formData.category_ids}
                            onChange={handleCategoryChange}
                            input={<OutlinedInput label="Categories *" />}
                            renderValue={(selected) => (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.5,
                                }}
                              >
                                {selected.map((value) => (
                                  <Chip
                                    key={value}
                                    label={getCategoryName(value)}
                                    size="small"
                                    onDelete={(e) => {
                                      e.stopPropagation();
                                      removeCategory(value);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    deleteIcon={<CloseIcon />}
                                    sx={{
                                      '& .MuiChip-deleteIcon': {
                                        color: '#666',
                                        '&:hover': {
                                          color: '#333',
                                        },
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          >
                            {categories.map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.category_ids && (
                            <Typography color="error" variant="caption">
                              {errors.category_ids}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      {filteredSubCategories.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Sub-Categories</InputLabel>
                            <Select
                              multiple
                              value={formData.sub_category_ids}
                              onChange={handleSubCategoryChange}
                              input={<OutlinedInput label="Sub-Categories" />}
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={getSubCategoryName(value)}
                                      size="small"
                                      onDelete={(e) => {
                                        e.stopPropagation();
                                        removeSubCategory(value);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      deleteIcon={<CloseIcon />}
                                      sx={{
                                        '& .MuiChip-deleteIcon': {
                                          color: '#666',
                                          '&:hover': {
                                            color: '#333',
                                          },
                                        },
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            >
                              {filteredSubCategories.map((subCategory) => (
                                <MenuItem
                                  key={subCategory.id}
                                  value={subCategory.id}
                                >
                                  {subCategory.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Tags"
                          value={rawTagInput}
                          onChange={handleTagsChange}
                          placeholder="Enter tags separated by commas (e.g., official, draft, internal)"
                          helperText="Tags help organize and categorize documents. Enter multiple tags separated by commas."
                          error={!!errors.tags}
                          multiline
                          rows={2}
                        />
                        {errors.tags && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            {errors.tags}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  )}

                  {index === 2 && (
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        You will be redirected to the Document Manager page
                        after creating the document, where you can view and
                        manage your documents.
                      </Alert>
                      <Typography variant="body1" gutterBottom>
                        Document Information Summary:
                      </Typography>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Name: <strong>{formData.name}</strong>
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Version: <strong>{formData.version}</strong>
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Description:{' '}
                                <strong>
                                  {formData.description || 'None'}
                                </strong>
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Categories:{' '}
                                <strong>
                                  {formData.category_ids
                                    .map(getCategoryName)
                                    .join(', ') || 'None'}
                                </strong>
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Sub-Categories:{' '}
                                <strong>
                                  {formData.sub_category_ids
                                    .map(getSubCategoryName)
                                    .join(', ') || 'None'}
                                </strong>
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Tags:{' '}
                                <strong>
                                  {formData.tags.join(', ') || 'None'}
                                </strong>
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Box>
                  )}

                  <Box sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={1}>
                      {index > 0 && <Button onClick={handleBack}>Back</Button>}
                      {index < steps.length - 1 ? (
                        <Button variant="contained" onClick={handleNext}>
                          Next
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={handleSubmit}
                          disabled={isCreating}
                          sx={{
                            backgroundColor: '#00A859',
                            '&:hover': { backgroundColor: '#008a4a' },
                          }}
                        >
                          {isCreating ? 'Creating...' : 'Create Document'}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>
    </Box>
  );
};

export default DocumentCreate;
